'use client';

import useSWR from 'swr';
import { useAuth } from './auth';
import { apiFetch } from './api';

export type NationalEnvironment = {
  country: string;
  countryName: string;
  firstDayOfWeek: number;   // 0=Sunday, 1=Monday
  weekendDays: number[];     // JS getDay() values
  holidayCountry: string;    // for /api/holidays?country=
  dateFormat: string;        // e.g. "dd.MM.yyyy"
  timeFormat: '24h' | '12h';
  currency: string;
  currencySymbol: string;
  decimalSeparator: string;
  thousandsSeparator: string;
  timezone: string;
  locale: string;            // for Intl.DateTimeFormat
};

const DEFAULT_ENV: NationalEnvironment = {
  country: 'CZ',
  countryName: 'CZ',
  firstDayOfWeek: 1,
  weekendDays: [0, 6],
  holidayCountry: 'cz',
  dateFormat: 'dd.MM.yyyy',
  timeFormat: '24h',
  currency: 'CZK',
  currencySymbol: 'Kč',
  decimalSeparator: ',',
  thousandsSeparator: ' ',
  timezone: 'Europe/Prague',
  locale: 'cs-CZ',
};

const LOCALE_TO_COUNTRY: Record<string, string> = {
  cs: 'CZ', sk: 'SK', de: 'DE', ru: 'RU', uk: 'UK', en: 'US',
};

/**
 * Resolves the national environment for the current user.
 * Resolution chain: user.country → system_settings.default_country → derive from locale → CZ.
 *
 * Fetches the environment from /api/settings/national-environment?country=XX.
 * Returns a default CZ preset while loading.
 */
let currentFormatLocale = DEFAULT_ENV.locale;

export function useNationalEnvironment() {
  const { isAuthenticated, mounted } = useAuth();

  // Fetch user profile for country override
  const { data: profile } = useSWR<{ country?: string | null; locale?: string | null }>(
    mounted && isAuthenticated ? '/api/auth/me' : null,
    (url: string) => apiFetch<{ country?: string | null; locale?: string | null }>(url, { silent: true }),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  // Fetch public settings for default_country
  const { data: publicSettings } = useSWR<{ default_country?: string; default_locale?: string }>(
    mounted ? '/api/settings/public' : null,
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  // Resolve country
  const country = profile?.country
    || publicSettings?.default_country
    || (profile?.locale ? LOCALE_TO_COUNTRY[profile.locale] : undefined)
    || (publicSettings?.default_locale ? LOCALE_TO_COUNTRY[publicSettings.default_locale] : undefined)
    || 'CZ';

  // Fetch the environment preset
  const { data, isLoading } = useSWR<NationalEnvironment>(
    country ? `/api/settings/national-environment?country=${country}` : null,
    (url: string) => apiFetch<NationalEnvironment>(url),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  // Keep module-level locale in sync for non-hook formatting call sites.
  // Assigned during render (idempotent) so getFormatLocale() sees the fresh value
  // in the same render pass — a useEffect would lag one render behind.
  // eslint-disable-next-line react-hooks/globals
  currentFormatLocale = (data || DEFAULT_ENV).locale;

  return {
    env: data || DEFAULT_ENV,
    country,
    isLoading,
  };
}

/**
 * Returns the Intl locale tag (e.g. "cs-CZ") for date/number/currency formatting.
 * Driven by the COUNTRY selector (national environment), NOT the UI language —
 * use this instead of `locale` from useTranslation() in toLocaleXxx / Intl.xxx calls.
 */
export function useFormatLocale(): string {
  const { env } = useNationalEnvironment();
  return env.locale;
}

/**
 * Non-hook variant for module-level helpers/constants that cannot call hooks.
 * Returns the last locale resolved by useNationalEnvironment (defaults to cs-CZ).
 * Note: does not subscribe to changes — prefer useFormatLocale() in components.
 */
export function getFormatLocale(): string {
  return currentFormatLocale;
}

/**
 * Update the current user's country preference.
 * Pass null/empty to reset to global default.
 */
export async function updateUserCountry(country: string | null): Promise<void> {
  await apiFetch('/api/auth/country', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country: country || '' }),
  });
}
